import { useEffect, useRef } from 'react';

interface MapyCzPanoramaInteractiveProps {
  lat: number;
  lng: number;
  height?: number;
  showNavigation?: boolean;
}

declare global {
  interface Window {
    Panorama?: {
      panoramaFromPosition: (options: {
        parent: HTMLElement;
        lon: number;
        lat: number;
        apiKey: string;
        yaw?: 'auto' | 'point' | number;
        pitch?: number;
        fov?: number;
        radius?: number;
        lang?: string;
        showNavigation?: boolean;
        hideErrors?: boolean;
      }) => {
        info?: {
          lon: number;
          lat: number;
          date: string;
        };
        error: string;
        errorCode: 'NONE' | 'PANORAMA_NOT_FOUND' | 'MISSING_API_KEY' | 'WRONG_API_KEY';
        destroy: () => void;
      };
    };
  }
}

export function MapyCzPanoramaInteractive({
  lat,
  lng,
  height = 400,
  showNavigation = true,
}: MapyCzPanoramaInteractiveProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<any>(null);

  useEffect(() => {
    const apiKey = import.meta.env['VITE_MAPY_API_KEY'] as string | undefined;

    if (!apiKey) {
      console.error('VITE_MAPY_API_KEY is not set');
      return;
    }

    if (!lat || !lng) {
      console.error('GPS coordinates are missing');
      return;
    }

    if (!containerRef.current) {
      return;
    }

    // Načíst JS Panorama script
    const script = document.createElement('script');
    script.src = 'https://api.mapy.cz/js/panorama/v1/panorama.js';
    script.async = true;

    script.onload = () => {
      if (!window.Panorama || !containerRef.current) {
        return;
      }

      try {
        // Vytvořit interaktivní panorama
        panoramaRef.current = window.Panorama.panoramaFromPosition({
          parent: containerRef.current,
          lon: lng,
          lat: lat,
          apiKey: apiKey,
          yaw: 'auto', // Automatický směr pohledu
          pitch: -0.066, // Mírný náklon dolů
          fov: 1.5, // Zorné pole
          radius: 100, // Hledat do 100m
          lang: 'cs',
          showNavigation: showNavigation, // Povolit navigaci mezi panoramaty
          hideErrors: false,
        });

        // Logování chyb
        if (panoramaRef.current.errorCode !== 'NONE') {
          console.error('Panorama error:', panoramaRef.current.error);
        }
      } catch (error) {
        console.error('Error creating panorama:', error);
      }
    };

    script.onerror = () => {
      console.error('Failed to load Mapy.cz Panorama script');
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      if (panoramaRef.current?.destroy) {
        panoramaRef.current.destroy();
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [lat, lng, showNavigation]);

  return (
    <div className="relative rounded-lg overflow-hidden bg-gray-100">
      <div
        ref={containerRef}
        style={{ width: '100%', height: `${height}px` }}
        className="panorama-container"
      />
      <div className="absolute bottom-2 right-2 bg-white/90 px-2 py-1 rounded text-xs text-gray-600">
        © Mapy.cz - Použijte myš nebo klávesy pro rozhlížení
      </div>
    </div>
  );
}
